/// <amd-module name="RMWidgets/DeploymentStatusWidget/DSWDataProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { Statuses } from "VSSUI/Status";

import * as TfsWidgetContext from "Tfs/Widget/Context";

import { WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";

import { DSWState } from "RMWidgets/DeploymentStatusWidget/DSWState";

import {
    IDSWCustomSettings,
    IDSWBuildInfo,
    IDSWEnvironmentInfo,
    IDSWDeploymentInfo,
    IDSWTestResultInfo,
    IDSWLastColumnInfo,
    DSWLastColumnType,
    WidgetSizeMapping,
    DSWViewType
} from "RMWidgets/DeploymentStatusWidget/DSWContracts";
import {
    getBuildTooltip,
    getFullBranchName,
    getReleaseDefinitionIds,
    constructEnvInfo,
    getDeploymentQueryParameters,
    getBuildInfoListFromDeployments,
    getBuildIdsFromDeploymentInfo,
    constructDeploymentInfo,
    getReleaseEnvironmentReferences,
    getTestReleaseReferences,
    getTestResultInfo,
    getTestPercentageStatusCssMap,
    getTestResultsStatusMap,
    getArtifactSourceIdFromBDId,
    getDefinitionTypeStringMapping
} from "RMWidgets/DeploymentStatusWidget/DSWUtils";
import * as Resources from "RMWidgets/DeploymentStatusWidget/Resources";

import {
    ReleaseDefinition,
    ReleaseDefinitionExpands,
    PropertySelector,
    PropertySelectorType,
    Deployment,
    DefinitionEnvironmentReference,
    DeploymentQueryParameters,
    DeploymentsQueryType,
    DeploymentExpands,
    DeploymentStatus,
    DeploymentOperationStatus
} from "Release/Client/Contracts/Release";
import { IReleaseRestClient, ReleaseClientName } from "Release/Client/RestClient/Release";
import { createBuildDefinitionUrl } from "Release/Helpers/Link";

import { BuildDefinition, DefinitionType, Build } from "RMWidgets/DeploymentStatusWidget/Clients/Contracts/Build";
import { IBuildRestClient, BuildClientName } from "RMWidgets/DeploymentStatusWidget/Clients/RestClient/Build";

import { ReleaseReference, TestResultSummary } from "RMWidgets/DeploymentStatusWidget/Clients/Contracts/Test";
import { ITestResultsRestClient, TestResultsClientName } from "RMWidgets/DeploymentStatusWidget/Clients/RestClient/TestResults";

/** Provides async method(s) to retrieve deployment status data from server */
export class DSWDataProvider {
    private projectId: string;
    private releaseRestClient: IReleaseRestClient;
    private testResultsMap: { [releaseEnvironmentId: number]: IDSWTestResultInfo };

    private static testResultsBatchSize = 50;

    constructor(private pageContext: IVssPageContext) {
        this.releaseRestClient = this.pageContext.getRestClient<IReleaseRestClient>(ReleaseClientName);
        this.testResultsMap = {};
    }

    public async getDSWStateData(settings: IDSWCustomSettings, size: WidgetSize): Promise<DSWState> {
        this.projectId = TfsWidgetContext.getProject(this.pageContext).id;

        // Build definition information.
        const buildRestClient = this.pageContext.getRestClient<IBuildRestClient>(BuildClientName);
        const buildDefinition = await buildRestClient.getDefinition(this.projectId, settings.buildDefinitionId);

        // Branch information.
        const branchName = this.getBranchInfo(settings, buildDefinition);

        // Builds information.
        const buildsCount = WidgetSizeMapping.getNumberOfBuilds(settings, size);
        const queryParams = getDeploymentQueryParameters(
            this.projectId,
            settings.environments,
            settings.buildDefinitionId,
            getDefinitionTypeStringMapping(DefinitionType.Build),
            branchName,
            [],
            buildsCount
        );
        const deployments = await this.releaseRestClient.getDeploymentsForMultipleEnvironments(queryParams, this.projectId);
        const builds = this.getBuilds(deployments, branchName, settings.buildDefinitionId, buildsCount);

        // Environments information.
        const environmentsInfo = await this.getEnvironmentsInfo(settings);

        // Map of deployments by definition environment id and build id.
        const deploymentsMap = this.getDeploymentsMap(deployments, settings.buildDefinitionId);

        // Map of test results by release environment id.
        await this.populateTestResultsMap(deployments, settings.viewTypeIdentifier, settings.viewType, settings.ruleBoundaries, settings.ruleOperators);

        // Map of information populating the last column on the widget, by definition environment id.
        const lastColumnMap = await this.getLastColumnMap(settings, branchName);

        return <DSWState>{
            branchName: branchName,
            builds: builds,
            environmentsInfo: environmentsInfo,
            deploymentsMap: deploymentsMap,
            testResultsMap: this.testResultsMap,
            lastColumnMap: lastColumnMap,
            buildDefinitionInfo: {
                name: buildDefinition.name,
                url: createBuildDefinitionUrl(this.pageContext, buildDefinition.id)
            }
        }

    }

    public async getEnvironmentsInfo(settings: IDSWCustomSettings) {
        const environmentsInfo: IDSWEnvironmentInfo[] = [];
        const releaseDefinitionIds = getReleaseDefinitionIds(settings.environments);
        const releaseDefinitions = await this.getReleaseDefinitions(releaseDefinitionIds);

        if (settings.environments.length > 0 && releaseDefinitions.length > 0) {
            settings.environments.forEach(environment => {
                const releaseDefinition = releaseDefinitions.find(rd => rd.id === environment.releaseDefinitionId);
                const releaseDefinitionEnvironment = releaseDefinition ? releaseDefinition.environments.find(env => env.id === environment.definitionEnvironmentId) : undefined;

                if (releaseDefinition && releaseDefinitionEnvironment) {
                    const environmentInfo = constructEnvInfo(this.pageContext, releaseDefinition, releaseDefinitionEnvironment);
                    environmentsInfo.push(environmentInfo);
                }
            });
        }

        return environmentsInfo;
    }

    public async getReleaseDefinitions(releaseDefinitionIds: number[]): Promise<ReleaseDefinition[]> {
        let definitionIdFilter = [];

        if (releaseDefinitionIds && releaseDefinitionIds.length > 0) {
            definitionIdFilter.push(releaseDefinitionIds.join(","));
        }

        const propertySelector = this.getPropertySelectorForReleaseDefinitions();
        return await this.releaseRestClient.getReleaseDefinitions(
            this.projectId,
            undefined,
            ReleaseDefinitionExpands.Environments,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            propertySelector.properties,
            definitionIdFilter
        );
    }

    public getBuilds(deployments: Deployment[], branchName: string, buildDefinitionId: number, buildsCount: number): IDSWBuildInfo[] {
        let buildInfoList = getBuildInfoListFromDeployments(deployments, buildDefinitionId, branchName);
        if (buildInfoList.length > buildsCount) {
            buildInfoList = buildInfoList.slice(buildInfoList.length - buildsCount);
        }

        let builds: IDSWBuildInfo[] = [];
        if (buildInfoList && buildInfoList.length) {
            builds = buildInfoList.map((build: Build) => {
                return <IDSWBuildInfo>{
                    id: build.id,
                    name: build.buildNumber,
                    tooltip: getBuildTooltip(build),
                    url: build.url || ""
                }
            })
        }

        return builds;
    }

    public getDeploymentsMap(deployments: Deployment[], buildDefinitionId: number): { [definitionEnvironmentId: number]: { [buildId: number]: IDSWDeploymentInfo } } {
        const deploymentsMap: { [definitionEnvironmentId: number]: { [buildId: number]: IDSWDeploymentInfo } } = {};

        if (deployments && deployments.length > 0) {
            deployments.forEach(deployment => {
                const buildIds = getBuildIdsFromDeploymentInfo(deployment, buildDefinitionId);
                const deploymentInfo = constructDeploymentInfo(this.pageContext, deployment);

                buildIds && buildIds.forEach((buildId: number) => {
                    const defEnvId = deployment.definitionEnvironmentId;
                    if (!deploymentsMap[defEnvId]) {
                        deploymentsMap[defEnvId] = {}
                    }

                    deploymentsMap[defEnvId][buildId] = deploymentInfo;
                });
            });
        }

        return deploymentsMap;
    }

    public async populateTestResultsMap(
        deployments: Deployment[],
        viewTypeIdentifier: number,
        viewType: string, // For back compatibility
        ruleBoundaries: string[],
        ruleOperators: string[]
    ): Promise<void> {
        let remainingList = getReleaseEnvironmentReferences(deployments);
        let testResultsList: TestResultSummary[][] = [];
        const testRestClient = this.pageContext.getRestClient<ITestResultsRestClient>(TestResultsClientName);

        while (remainingList.length > 0) {
            const batch = remainingList.splice(0, DSWDataProvider.testResultsBatchSize - 1);
            const releaseRefs: ReleaseReference[] = getTestReleaseReferences(this.projectId, batch);
            const testResults = await testRestClient.queryTestResultsSummaryForReleases(releaseRefs, this.projectId);
            testResultsList.push(testResults);
        }

        testResultsList.forEach((testResults: TestResultSummary[]) => {
            if (testResults) {
                testResults.forEach(testResult => {
                    const releaseReference = testResult.testResultsContext.release;
                    const cell = getTestResultInfo(this.pageContext, testResult);

                    if (DSWViewType.checkViewType(viewType,
                            DSWViewType.TestPassRate,
                            viewTypeIdentifier,
                            DSWViewType.TestPassRateIdentifier)) {
                        cell.testStatusColor = getTestPercentageStatusCssMap(cell.passPercentage, ruleBoundaries, ruleOperators);
                        cell.testStatusProps = getTestResultsStatusMap(cell.testStatusColor);
                    } else {
                        cell.testStatusColor = "";
                        cell.testStatusProps = Statuses.Queued;
                    }

                    this.testResultsMap[releaseReference.environmentId] = cell;
                });
            }
        });
    }

    public async getLastColumnMap(settings: IDSWCustomSettings, branchName: string): Promise<{ [definitionEnvironmentId: number]: IDSWLastColumnInfo }> {
        let lastColumnMap: { [definitionEnvironmentId: number]: IDSWLastColumnInfo } = {};
        let deployments: Deployment[] = [];

        if (settings.lastColumnType === DSWLastColumnType.LastDeployed) {
            let queryParameters = this.getLastDeployedQueryParameters(
                settings.environments,
                settings.buildDefinitionId,
                getDefinitionTypeStringMapping(DefinitionType.Build),
                branchName);

            deployments = await this.releaseRestClient.getDeploymentsForMultipleEnvironments(queryParameters, this.projectId);
        }

        if (settings.lastColumnType === DSWLastColumnType.FailingSince) {
            let queryParameters: DeploymentQueryParameters = <DeploymentQueryParameters>{
                environments: settings.environments,
                artifactSourceId: getArtifactSourceIdFromBDId(this.projectId, settings.buildDefinitionId),
                artifactTypeId: getDefinitionTypeStringMapping(DefinitionType.Build),
                sourceBranch: branchName,
                expands: DeploymentExpands.Artifacts,
                queryType: DeploymentsQueryType.FailingSince
            };

            deployments = await this.releaseRestClient.getDeploymentsForMultipleEnvironments(queryParameters, this.projectId);
        }

        await this.populateTestResultsMap(deployments, settings.viewTypeIdentifier, settings.viewType, settings.ruleBoundaries, settings.ruleOperators);

        deployments.forEach(deployment => {
            let deploymentInfo: IDSWLastColumnInfo = <IDSWLastColumnInfo>constructDeploymentInfo(this.pageContext, deployment);
            let builds = getBuildInfoListFromDeployments([deployment], settings.buildDefinitionId, branchName);

            if (builds && builds.length > 0) {
                let buildInfo = builds[builds.length - 1];
                deploymentInfo.buildId = buildInfo.id;
                deploymentInfo.buildName = buildInfo.buildNumber;
                deploymentInfo.buildUrl = buildInfo.url;
                deploymentInfo.toolTip.build = buildInfo.buildNumber;
                deploymentInfo.toolTip.buildStatus = Resources.DSWBuildStatusCompleted;
            }

            lastColumnMap[deployment.definitionEnvironmentId] = deploymentInfo;
        });

        return lastColumnMap;
    }

    public getLastDeployedQueryParameters(
        environmentReference: DefinitionEnvironmentReference[],
        buildDefinitionId: number,
        artifactType: string,
        sourceBranch: string
    ): DeploymentQueryParameters {
        let deploymentExpands: DeploymentExpands = DeploymentExpands.Artifacts;
        let deploymentStatus: DeploymentStatus = DeploymentStatus.Failed
            | DeploymentStatus.PartiallySucceeded
            | DeploymentStatus.Succeeded;

        let queryParameters = <DeploymentQueryParameters>{
            artifactSourceId: getArtifactSourceIdFromBDId(this.projectId, buildDefinitionId),
            artifactTypeId: artifactType,
            artifactVersions: <string[]>[],
            sourceBranch: sourceBranch,
            deploymentStatus: deploymentStatus,
            operationStatus: DeploymentOperationStatus.All,
            environments: environmentReference,
            deploymentsPerEnvironment: 1,
            expands: deploymentExpands,
            queryType: DeploymentsQueryType.Regular
        };

        return queryParameters;
    }

    public getBranchInfo(settings: IDSWCustomSettings, buildDefinition: BuildDefinition): string {
        if (settings.useDefaultBranch || !settings.branchName) {
            return getFullBranchName(buildDefinition.repository.type, buildDefinition.repository.defaultBranch);
        } else {
            const repositoryType = settings.repository ? settings.repository.type : "";
            return getFullBranchName(repositoryType, settings.branchName);
        }
    }

    private getPropertySelectorForReleaseDefinitions(): PropertySelector {
        const propsToInclude: string[] = [
            "Id",
            "Name",
            "Environments.Id",
            "Environments.Name"
        ];

        return {
            selectorType: PropertySelectorType.Inclusion,
            properties: propsToInclude
        };
    }
}
